import { ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/** How far a press may move and still be a tap or a click (px) */
const TAP_MOVE = 8;
/** Two taps this close in time (ms) and place (px) are a double tap */
const DOUBLE_TAP_TIME = 300;
const DOUBLE_TAP_GAP = 40;
/** A swipe moves to the next photo past this share of the screen, or this fast (px per ms) */
const SWIPE_SHARE = 0.18;
const SWIPE_SPEED = 0.35;
/** A click or a double tap zooms to the photo's full resolution, at least 2 times and at most 4 */
const ZOOM_MIN_STEP = 2;
const ZOOM_MAX = 4;
/** An arrow key pans the zoomed photo by this share of the screen */
const KEY_PAN = 0.2;

/**
 * The zoom view as a slideshow (THEME.md §7.9 **Gallery**, §7.13, §9.4 #54; T-237; DESIGN.md › Components › Product
 * gallery). `snippets/okt-zoom-slideshow.liquid` renders this element inside Horizon's zoom dialog, with the previous
 * and next buttons and the counter when there's more than one photo; its stylesheet lays the photos out in a row
 * that snaps one photo at a time and gives the dialog every touch (`touch-action: none`), so a pinch or a double tap
 * zooms the photo, not the page. The page itself keeps the browser's zoom.
 *
 * Horizon's zoom dialog still opens on the photo clicked with its morph, closes (its button, Escape) and keeps the
 * page's gallery on the photo shown. This adds:
 * - Moving between photos: the arrows and the arrow keys (one step glides, Home, End and reduced motion at once,
 *   through Horizon's selectThumbnail), a swipe (ours: the row follows the finger and settles on the next photo) and
 *   a trackpad (the row scrolls). The counter follows and announces "Slide 2 of 5"; the arrows are muted at the ends;
 *   the slides beside the shown one are inert (Tab stays on the shown one; a video there stops: okt-video-autoplay
 *   pauses inert videos, and a video the visitor started is paused here). The neighbours' photos load early.
 * - Zoom, on photos only, as boxraw.com's (PhotoSwipe): a click (mouse) or a double tap zooms into the point to the
 *   photo's full resolution (2 to 4 times); a drag, one finger or a trackpad pans it; a pinch or a trackpad pinch
 *   zooms between 1 and 4 times around the fingers; a click or a double tap zooms out. A swipe changes photos only
 *   while not zoomed. Zooms by click, tap or key glide on the media tokens, at once under reduced motion. The photo
 *   shown is a button ("Zoom in", "Zoom out"): Enter, Space, + and − zoom, the arrow keys pan while zoomed, Escape
 *   zooms out first, then closes. A click beside the photo closes the view, as Horizon's click on a photo did; a tap
 *   there does nothing, so a near miss of a double tap doesn't close it.
 */
class OktZoomSlideshow extends HTMLElement {
  /** @type {HTMLElement | null} Horizon's zoom-dialog */
  zoom = null;

  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLElement | null} The dialog's row of slides */
  list = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** The photo shown */
  #index = -1;

  #frame = 0;

  /** The shown photo's zoom: scale and translation (px) from where it sits whole */
  #scale = 1;
  #x = 0;
  #y = 0;

  /** @type {Map<number, { x: number, y: number }>} */
  #pointers = new Map();

  /** @type {any} The gesture under way */
  #gesture = null;

  /** @type {{ t: number, x: number, y: number } | null} */
  #lastTap = null;

  #pointerType = 'mouse';

  #suppressClick = false;

  /** The row's width when the photo was zoomed */
  #zoomWidth = 0;

  connectedCallback() {
    const zoom = this.closest('zoom-dialog');
    const dialog = this.closest('dialog');
    const list = dialog?.querySelector('.dialog-zoomed-gallery');
    if (!(zoom instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement) || !(list instanceof HTMLElement)) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.zoom = zoom;
    this.dialog = dialog;
    this.list = list;
    // The row scrolls, so Chrome would give it a Tab stop of its own; the arrows and the keys move it
    if (!list.hasAttribute('tabindex')) list.tabIndex = -1;

    this.addEventListener('click', this.#onControlClick, { signal });
    list.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    list.addEventListener('pointerdown', this.#onPointerDown, { signal });
    list.addEventListener('click', this.#onClick, { signal });
    list.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    // A photo dragged with the mouse pans, never starts the browser's drag of the image
    list.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    // Safari's own pinch and its touch scroll, which touch-action alone may not stop
    list.addEventListener('gesturestart', (event) => event.preventDefault(), { signal });
    list.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length > 1 || this.#gesture) event.preventDefault();
      },
      { signal, passive: false }
    );
    // Before Horizon's handlers, which listen on the document while capturing: Escape zooms out first
    window.addEventListener('keydown', this.#onKeyDown, { signal, capture: true });
    // The close button: back to the whole photo first, so Horizon's morph goes from it
    window.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (dialog.open && target?.closest('.dialog-zoomed-gallery__close-button') && dialog.contains(target)) {
          this.#reset();
        }
      },
      { signal, capture: true }
    );
    // A new screen size (a turned phone) puts the photo back whole; Safari's bars coming and going don't
    window.addEventListener(
      'resize',
      () => {
        if (this.#scale > 1 && list.clientWidth !== this.#zoomWidth) this.#reset();
      },
      { signal }
    );
    // Horizon's dialog selects a photo when it opens with its morph and when it settles after a scroll
    zoom.addEventListener(ThemeEvents.zoomMediaSelected, this.#onSelected, { signal });

    // Opening without the morph (no view transitions) selects nothing: read the photo Horizon scrolled to. Closing
    // leaves no zoom behind.
    this.#observer = new MutationObserver(() => {
      if (dialog.open) this.#show(this.#current(), true);
      else this.#reset();
    });
    this.#observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#endGesture();
    cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement[]} The dialog's slides */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.list?.children ?? []));
  }

  /** The shown slide */
  get #slide() {
    return this.#slides[this.#index] ?? null;
  }

  /** The shown photo's box, the element that zooms: photos only, never a video */
  get #box() {
    const slide = this.#slide;
    if (!slide?.classList.contains('product-media-container--image')) return null;
    return /** @type {HTMLElement | null} */ (slide.querySelector(':scope > .product-media'));
  }

  get #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /** The slide nearest the row's scroll position (negative in right-to-left languages) */
  #current() {
    const list = /** @type {HTMLElement} */ (this.list);
    const width = list.clientWidth || 1;
    return Math.min(this.#slides.length - 1, Math.max(0, Math.round(Math.abs(list.scrollLeft) / width)));
  }

  /** @param {Event} event */
  #onSelected = (event) => {
    const { index } = /** @type {{ detail?: { index?: number } }} */ (event).detail ?? {};
    if (typeof index === 'number') this.#show(index);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#show(this.#current());
      // Horizon's own check (debounced): the high-resolution photo and the page's gallery follow
      if (this.#gesture?.type !== 'swipe') /** @type {any} */ (this.zoom).handleScroll?.();
    });
  };

  /** The previous and next buttons @param {MouseEvent} event */
  #onControlClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-disabled') === 'true') return;
    this.#go(this.#index + Number(button.dataset.step));
  };

  /** @param {number} index */
  #go(index) {
    const target = Math.min(this.#slides.length - 1, Math.max(0, index));
    if (target === this.#index) return;

    // One step glides; Home, End and reduced motion go at once, so nothing slides past a run of photos
    const behavior = prefersReducedMotion() || Math.abs(target - this.#index) > 1 ? 'instant' : 'smooth';
    const zoom = /** @type {any} */ (this.zoom);
    if (typeof zoom.selectThumbnail === 'function') zoom.selectThumbnail(target, { behavior });
    else this.#slides[target]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    this.#show(target);
  }

  /**
   * Shows the slide's number, readies its neighbours and makes its photo the zoom button
   * @param {number} index
   * @param {boolean} [force] - Also when the number hasn't changed (the dialog opened)
   */
  #show(index, force = false) {
    const slides = this.#slides;
    if (!slides.length || (index === this.#index && !force)) return;
    if (index !== this.#index) this.#reset();
    this.#index = index;

    const current = this.querySelector('[data-current]');
    if (current) current.textContent = String(index + 1);
    const status = this.querySelector('[data-status-text]');
    const words = this.dataset.status
      ?.replace('[index]', String(index + 1))
      .replace('[length]', String(slides.length));
    if (status && words && status.textContent !== words) status.textContent = words;

    for (const button of this.querySelectorAll('[data-step]')) {
      const step = Number(/** @type {HTMLElement} */ (button).dataset.step);
      const end = step < 0 ? index === 0 : index === slides.length - 1;
      button.setAttribute('aria-disabled', String(end));
    }

    slides.forEach((slide, i) => {
      const away = i !== index;
      if (slide.inert !== away) slide.inert = away;
      // The neighbours' photos load before they're shown
      if (Math.abs(i - index) === 1) {
        for (const image of slide.querySelectorAll('img[loading="lazy"]')) image.setAttribute('loading', 'eager');
      }
      // A video the visitor started stops when its slide goes
      if (away) {
        for (const media of slide.querySelectorAll('deferred-media[data-media-loaded]')) {
          /** @type {any} */ (media).pauseMedia?.();
        }
      }
      // The shown photo is the zoom button; the others aren't
      const box = slide.classList.contains('product-media-container--image')
        ? slide.querySelector(':scope > .product-media')
        : null;
      if (!(box instanceof HTMLElement)) return;
      if (away) {
        box.removeAttribute('tabindex');
        box.removeAttribute('role');
        box.removeAttribute('aria-label');
      } else {
        box.tabIndex = 0;
        box.setAttribute('role', 'button');
        box.setAttribute('aria-label', this.dataset.zoomIn || '');
      }
    });
  }

  /* Zoom ------------------------------------------------------------------------------------------------------- */

  /**
   * Where the shown photo sits whole: its box's place without the zoom, and the area it may fill zoomed, the slide
   * between the close button's band and the controls' (the slide's padding)
   */
  #geometry() {
    const box = this.#box;
    const slide = this.#slide;
    if (!box || !slide) return null;
    const rect = slide.getBoundingClientRect();
    const style = getComputedStyle(slide);
    const above = parseFloat(style.paddingTop) || 0;
    const below = parseFloat(style.paddingBottom) || 0;
    const area = { left: rect.left, top: rect.top + above, width: rect.width, height: rect.height - above - below };
    const width = box.offsetWidth;
    const height = box.offsetHeight;
    return {
      area,
      width,
      height,
      left: area.left + (area.width - width) / 2,
      top: area.top + (area.height - height) / 2,
    };
  }

  /** The zoom of a click or a double tap: the photo's full resolution, 2 to 4 times its size whole */
  #stepScale() {
    const box = this.#box;
    const image = box?.querySelector('img');
    const natural = image instanceof HTMLImageElement ? image.naturalWidth : 0;
    const full = box && box.offsetWidth ? natural / box.offsetWidth : 0;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN_STEP, full));
  }

  /**
   * Keeps the zoomed photo over the whole screen where it's larger than it, centred where it's smaller
   * @param {number} x @param {number} y @param {number} scale
   * @param {{ area: { left: number, top: number, width: number, height: number }, width: number, height: number, left: number, top: number }} g - from #geometry
   */
  #clamp(x, y, scale, g) {
    const offsetX = g.left - g.area.left;
    const offsetY = g.top - g.area.top;
    const width = g.width * scale;
    const height = g.height * scale;
    const clampX =
      width <= g.area.width
        ? (g.area.width - width) / 2 - offsetX
        : Math.min(-offsetX, Math.max(g.area.width - width - offsetX, x));
    const clampY =
      height <= g.area.height
        ? (g.area.height - height) / 2 - offsetY
        : Math.min(-offsetY, Math.max(g.area.height - height - offsetY, y));
    return [clampX, clampY];
  }

  /**
   * Zooms the shown photo to `scale`, keeping the point under `point` (the photo's middle by default) in place
   * @param {number} scale @param {{ x: number, y: number } | null} [point] @param {boolean} [animate]
   */
  #zoomTo(scale, point = null, animate = true) {
    const g = this.#geometry();
    if (!g) return;
    const next = scale < 1.02 ? 1 : Math.min(ZOOM_MAX, scale);
    const px = point?.x ?? g.area.left + g.area.width / 2;
    const py = point?.y ?? g.area.top + g.area.height / 2;
    const u = (px - g.left - this.#x) / this.#scale;
    const v = (py - g.top - this.#y) / this.#scale;
    const [x, y] = this.#clamp(px - g.left - u * next, py - g.top - v * next, next, g);
    this.#apply(next, x, y, animate);
  }

  /** @param {number} dx @param {number} dy @param {boolean} [animate] */
  #panBy(dx, dy, animate = false) {
    const g = this.#geometry();
    if (!g || this.#scale <= 1) return;
    const [x, y] = this.#clamp(this.#x + dx, this.#y + dy, this.#scale, g);
    this.#apply(this.#scale, x, y, animate);
  }

  /** @param {{ x: number, y: number } | null} point */
  #toggleZoom(point) {
    if (this.#scale > 1) this.#zoomTo(1, point);
    else this.#zoomTo(this.#stepScale(), point);
  }

  /** @param {number} scale @param {number} x @param {number} y @param {boolean} animate */
  #apply(scale, x, y, animate) {
    const box = this.#box;
    const slide = this.#slide;
    if (!box || !slide) return;
    this.#scale = scale;
    this.#x = scale === 1 ? 0 : x;
    this.#y = scale === 1 ? 0 : y;
    this.#zoomWidth = this.list?.clientWidth ?? 0;
    box.classList.toggle('okt-zoom-animate', animate && !prefersReducedMotion());
    box.style.transform = scale === 1 ? '' : `translate(${this.#x}px, ${this.#y}px) scale(${scale})`;
    slide.toggleAttribute('data-okt-zoomed', scale > 1);
    box.setAttribute('aria-label', (scale > 1 ? this.dataset.zoomOut : this.dataset.zoomIn) || '');
  }

  /** Every photo back whole, at once */
  #reset() {
    for (const slide of this.#slides) {
      slide.removeAttribute('data-okt-zoomed');
      slide.removeAttribute('data-okt-panning');
      const box = slide.querySelector(':scope > .product-media');
      if (box instanceof HTMLElement && box.style.transform) {
        box.classList.remove('okt-zoom-animate');
        box.style.transform = '';
        if (box.getAttribute('role') === 'button') box.setAttribute('aria-label', this.dataset.zoomIn || '');
      }
    }
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
  }

  /* Pointers: tap, double tap, drag, swipe, pinch ---------------------------------------------------------------- */

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerType = event.pointerType;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size === 1) {
      const { signal } = /** @type {AbortController} */ (this.#controller);
      window.addEventListener('pointermove', this.#onPointerMove, { signal });
      window.addEventListener('pointerup', this.#onPointerUp, { signal });
      window.addEventListener('pointercancel', this.#onPointerUp, { signal });
      const box = this.#box;
      this.#suppressClick = false;
      this.#gesture = {
        type: 'press',
        x0: event.clientX,
        y0: event.clientY,
        t: event.timeStamp,
        x: this.#x,
        y: this.#y,
        scroll: this.list?.scrollLeft ?? 0,
        moved: false,
        onPhoto: Boolean(box && event.target instanceof Node && box.contains(event.target)),
        speed: 0,
      };
      return;
    }

    // A second finger on a photo: a pinch, from where the row is
    if (this.#pointers.size === 2 && this.#box) {
      if (this.#gesture?.type === 'swipe') this.#settleSwipe(0);
      const g = this.#geometry();
      if (!g) return;
      const [a, b] = [...this.#pointers.values()];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.#gesture = {
        type: 'pinch',
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: this.#scale,
        u: (mid.x - g.left - this.#x) / this.#scale,
        v: (mid.y - g.top - this.#y) / this.#scale,
        moved: true,
      };
      this.#suppressClick = true;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = this.#gesture;
    if (!gesture) return;

    if (gesture.type === 'pinch') {
      if (this.#pointers.size < 2) return;
      const g = this.#geometry();
      if (!g) return;
      const [a, b] = [...this.#pointers.values()];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const scale = Math.min(ZOOM_MAX, Math.max(1, (gesture.scale * Math.hypot(a.x - b.x, a.y - b.y)) / gesture.distance));
      const [x, y] = this.#clamp(mid.x - g.left - gesture.u * scale, mid.y - g.top - gesture.v * scale, scale, g);
      this.#apply(scale, x, y, false);
      return;
    }

    const dx = event.clientX - gesture.x0;
    const dy = event.clientY - gesture.y0;
    if (!gesture.moved && Math.hypot(dx, dy) < TAP_MOVE) return;
    gesture.moved = true;

    // Zoomed: one finger or the mouse pans the photo
    if (this.#scale > 1 && (gesture.type === 'press' || gesture.type === 'pan')) {
      gesture.type = 'pan';
      this.#slide?.setAttribute('data-okt-panning', '');
      const g = this.#geometry();
      if (!g) return;
      const [x, y] = this.#clamp(gesture.x + dx, gesture.y + dy, this.#scale, g);
      this.#apply(this.#scale, x, y, false);
      return;
    }

    // Whole: a finger swiping sideways moves the row (the mouse and the trackpad have the arrows and the scroll)
    if (event.pointerType === 'mouse' || !this.list) return;
    if (gesture.type === 'press') {
      if (Math.abs(dx) < Math.abs(dy)) {
        gesture.type = 'vertical';
        return;
      }
      gesture.type = 'swipe';
      this.list.style.scrollSnapType = 'none';
    }
    if (gesture.type !== 'swipe') return;
    const elapsed = event.timeStamp - (gesture.lastT ?? gesture.t) || 16;
    gesture.speed = (dx - (gesture.lastDx ?? 0)) / elapsed;
    gesture.lastDx = dx;
    gesture.lastT = event.timeStamp;
    this.list.scrollLeft = gesture.scroll - dx;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.delete(event.pointerId);
    const gesture = this.#gesture;

    if (gesture?.type === 'pinch') {
      if (this.#pointers.size === 1) {
        // The finger left on the photo pans it from here
        const [p] = [...this.#pointers.values()];
        this.#gesture = { type: 'pan', x0: p.x, y0: p.y, t: event.timeStamp, x: this.#x, y: this.#y, moved: true };
        return;
      }
      if (this.#pointers.size === 0) {
        if (this.#scale < 1.02) this.#zoomTo(1);
        this.#endGesture();
      }
      return;
    }
    if (this.#pointers.size > 0) return;
    if (!gesture) return this.#endGesture();

    if (gesture.type === 'swipe') {
      this.#settleSwipe(gesture.speed ?? 0);
      this.#suppressClick = true;
    } else if (gesture.moved) {
      this.#suppressClick = true;
    } else if (event.type === 'pointerup' && event.pointerType !== 'mouse' && gesture.onPhoto) {
      // A tap on the photo: the second of two zooms in or out
      const last = this.#lastTap;
      const point = { x: event.clientX, y: event.clientY };
      if (last && event.timeStamp - last.t < DOUBLE_TAP_TIME && Math.hypot(point.x - last.x, point.y - last.y) < DOUBLE_TAP_GAP) {
        this.#lastTap = null;
        this.#toggleZoom(point);
      } else {
        this.#lastTap = { t: event.timeStamp, ...point };
      }
    }
    this.#endGesture();
  };

  /** Settles a swipe on the nearest photo, or the next one in the direction of a fast or long enough swipe @param {number} speed */
  #settleSwipe(speed) {
    const list = this.list;
    if (!list) return;
    const width = list.clientWidth || 1;
    const start = Math.round(Math.abs(this.#gesture?.scroll ?? 0) / width);
    const position = Math.abs(list.scrollLeft) / width;
    let target = Math.round(position);
    const moved = position - start;
    // The finger's direction, in the row's terms (right-to-left rows count the other way)
    const forward = (this.#rtl ? 1 : -1) * Math.sign(speed);
    if (Math.abs(speed) > SWIPE_SPEED && forward !== 0) target = start + forward;
    else if (Math.abs(moved) > SWIPE_SHARE) target = start + Math.sign(moved);
    target = Math.min(this.#slides.length - 1, Math.max(0, target));
    list.style.scrollSnapType = '';
    list.scrollTo({ left: (this.#rtl ? -1 : 1) * target * width, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    this.#show(target);
    /** @type {any} */ (this.zoom).handleScroll?.();
  }

  #endGesture() {
    this.#slide?.removeAttribute('data-okt-panning');
    this.#gesture = null;
    if (this.#pointers.size) return;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
  }

  /** A click on the photo zooms (the mouse; a finger zooms with a double tap); beside it, it closes @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#suppressClick) {
      this.#suppressClick = false;
      event.preventDefault();
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    const slide = target?.closest('.dialog-zoomed-gallery > li');
    if (!slide?.classList.contains('product-media-container--image')) return;
    const box = slide.querySelector(':scope > .product-media');
    const mouse = this.#pointerType === 'mouse';
    if (box?.contains(target)) {
      // A click from the keyboard (Enter or Space on the photo) has no pointer position: the middle
      if (mouse || event.detail === 0) this.#toggleZoom(event.detail === 0 ? null : { x: event.clientX, y: event.clientY });
      return;
    }
    if (!mouse) return;
    if (this.#scale > 1) this.#zoomTo(1);
    else /** @type {any} */ (this.zoom).close?.();
  };

  /** A trackpad: a pinch zooms, a scroll pans while zoomed (whole, the row scrolls) @param {WheelEvent} event */
  #onWheel = (event) => {
    if (!this.#box) return;
    if (event.ctrlKey) {
      event.preventDefault();
      this.#zoomTo(this.#scale * Math.exp(-event.deltaY * 0.01), { x: event.clientX, y: event.clientY }, false);
      return;
    }
    if (this.#scale <= 1) return;
    event.preventDefault();
    this.#panBy(-event.deltaX, -event.deltaY);
  };

  /* Keys -------------------------------------------------------------------------------------------------------- */

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const dialog = this.dialog;
    if (!dialog?.open || !(event.target instanceof Node) || !dialog.contains(event.target)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const zoomed = this.#scale > 1;
    const box = this.#box;
    const onPhoto = Boolean(box && event.target === box);
    /** @param {() => void} action */
    const take = (action) => {
      event.preventDefault();
      event.stopPropagation();
      action();
    };

    switch (event.key) {
      case 'Escape':
        if (zoomed) take(() => this.#zoomTo(1));
        return;
      case '+':
      case '=':
        if (box) take(() => this.#zoomTo(this.#stepScale()));
        return;
      case '-':
      case '_':
        if (box) take(() => this.#zoomTo(1));
        return;
      case 'Enter':
      case ' ':
        if (onPhoto) take(() => this.#toggleZoom(null));
        return;
    }

    const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (event.key in arrows) {
      const [ax, ay] = arrows[/** @type {keyof typeof arrows} */ (event.key)];
      if (zoomed) {
        const g = this.#geometry();
        if (g) take(() => this.#panBy(-ax * g.area.width * KEY_PAN, -ay * g.area.height * KEY_PAN, true));
        return;
      }
      if (ax) take(() => this.#go(this.#index + (this.#rtl ? -ax : ax)));
      return;
    }
    if (!zoomed && (event.key === 'Home' || event.key === 'End')) {
      take(() => this.#go(event.key === 'Home' ? 0 : this.#slides.length - 1));
    }
  };
}

if (!customElements.get('okt-zoom-slideshow')) customElements.define('okt-zoom-slideshow', OktZoomSlideshow);
